import {
  ContentChild,
  ContentChildren,
  Directive,
  Input,
  OnInit,
  QueryList,
} from '@angular/core';
import { BehaviorSubject, Observable, merge } from 'rxjs';
import { filter, map, scan, shareReplay } from 'rxjs/operators';
import {
  ExpandableBehavior,
  ExpandableState,
} from '../domain/table-expandable-state';
import { ExpandableBehaviorType, ExpandableRow } from '../domain/types';
import { TableColumnDirective } from './table-column.directive';
import { TableExpandableContentDirective } from './table-expandable-content.directive';

@Directive({
  selector: '[psUiTableExpandableRow]',
})
export class TableExpandableRowDirective<T extends Record<string, unknown> = {}>
  implements OnInit
{
  @ContentChildren(TableColumnDirective)
  public columns!: QueryList<TableColumnDirective>;

  @ContentChild(TableExpandableContentDirective)
  public content!: TableExpandableContentDirective;

  /*
   * Define the expandable behavior
   * 'one' - Expand one at time
   * 'each' - Expand each independently
   * */
  @Input()
  public behavior: ExpandableBehaviorType = 'one';
  @Input()
  public manualClose: boolean = false;

  @Input()
  public backGroundRowColor?: (element: T) => string;

  public get template() {
    return this.content?.template;
  }

  public expanded$!: Observable<ExpandableBehavior>;

  private readonly expandedCommand$ = new BehaviorSubject<ExpandableRow>(
    'none'
  );
  private readonly retractedCommand$ = new BehaviorSubject<ExpandableRow>(
    'none'
  );

  public ngOnInit(): void {
    const expand$ = this.expandedCommand$.pipe(
      map((row) => ({ row, type: 'expand' }))
    );

    const retract$ = this.retractedCommand$.pipe(
      filter((row) => row === 'all' || this.manualClose),
      map((row) => ({ row, type: 'retract' }))
    );

    this.expanded$ = merge(expand$, retract$).pipe(
      scan(
        (state, { row, type }) =>
          type === 'expand' ? state.expand(row) : state.retract(row),
        ExpandableState.init(this.behavior, this.manualClose)
      ),
      shareReplay(1)
    );
  }

  public expand(row: number): void {
    this.expandedCommand$.next(row);
  }

  public retract(row: number): void {
    this.retractedCommand$.next(row);
  }

  public reset(): void {
    this.retractedCommand$.next('all');
  }

  public getRowBackgroundColor(element: T) {
    return this.backGroundRowColor?.(element) || '';
  }
}
